// @ts-strict-ignore
import React, { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box as AgreementLinks,
  Box,
  Button,
  Callout,
  Checkbox,
  Dialog,
  Icon,
  RowsSkeletonLoader,
  toast,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { usePendingAgreementsMutation } from 'hooks/mutations/usePendingAgreementsMutation'
import { LegalAgreementStatus } from 'pages/LegalAgreements/types'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { logError } from 'utils/sentry'
import {
  AccountAgreementAcceptancePayload,
  AccountAgreementStatus,
  AgreementModalData,
} from './types'

export const PENDING_AGREEMENTS_FORM_NAME = 'pending agreements form'

export interface AgreementsModalProps {
  agreements: AgreementModalData[]
  isLoadingAgreements: boolean
  onClose: () => void
}

export const AgreementModalBody = ({
  agreements,
  isLoadingAgreements,
  onClose,
}: AgreementsModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const {
    mutate: confirmAgreements,
    isPending: isLoadingConfirm,
    isError: isErrorConfirm,
  } = usePendingAgreementsMutation()

  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [submitErrorMessage, setSubmitErrorMessage] = useState('')

  const currentStep = activeStepIndex + 1
  const totalSteps = agreements?.length
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex + 1 === agreements?.length

  const activeAgreement: AgreementModalData = agreements?.[activeStepIndex]

  const submitAgreementsAcceptance = () => {
    setSubmitErrorMessage('')
    const confirmedAgreements: AccountAgreementAcceptancePayload[] = agreements?.map(confirmed => ({
      id: confirmed?.agreementId,
      status: confirmed?.status,
    }))

    confirmAgreements(confirmedAgreements, {
      onSuccess: () => {
        onClose()
        toast({ header: t('pendingAgreements.submitSuccess'), variant: 'success' })
        if (window.location.pathname.includes(PathNames.LEGAL_AGREEMENTS)) {
          queryClient.invalidateQueries({
            queryKey: reactQueryKeys.legalAgreements([
              LegalAgreementStatus.PENDING,
              LegalAgreementStatus.SIGNED,
            ]),
          })
        }
      },
      onError: (error: BaseErrorResponse) => {
        let errorMessage = t('pendingAgreements.submitError')
        if (error?.status === 403) {
          errorMessage = t('pendingAgreements.permissionsError')
        } else {
          logError('Error from AgreementModal - confirmAgreements', error)
        }
        setSubmitErrorMessage(errorMessage)
      },
    })
  }

  const handleContinue = useCallback(() => {
    isLastStep ? submitAgreementsAcceptance() : setActiveStepIndex(current => current + 1)
  }, [isLastStep, submitAgreementsAcceptance])

  const handleBack = useCallback(() => {
    if (showDeclineConfirmation) {
      setShowDeclineConfirmation(false)
    } else if (!isFirstStep) {
      activeAgreement.status = isChecked
        ? AccountAgreementStatus.SIGNED
        : AccountAgreementStatus.PENDING
      setActiveStepIndex(current => current - 1)
    }
  }, [showDeclineConfirmation, isFirstStep, activeStepIndex, isChecked])

  const handleAccept = useCallback(() => {
    activeAgreement.status = AccountAgreementStatus.SIGNED
    handleContinue()
  }, [activeStepIndex, handleContinue])

  const handleDecline = useCallback(() => {
    activeAgreement.status = AccountAgreementStatus.DECLINED
    setShowDeclineConfirmation(false)
    handleContinue()
  }, [activeStepIndex, handleContinue])

  useEffect(() => {
    const isActiveChecked = activeAgreement?.status === AccountAgreementStatus.SIGNED
    setIsChecked(isActiveChecked)
  }, [activeStepIndex])

  const showBackButton = !isFirstStep || showDeclineConfirmation

  const BackButton = (
    <Button key="back" variant="secondary" onClick={handleBack}>
      {t('back')}
    </Button>
  )

  // TODO: Add decline button - not supported by dialog to have 3 buttons
  const DeclineButton = (
    <Button key="decline" onClick={handleDecline}>
      {t('decline')}
    </Button>
  )

  const ConfirmDeclineButton = (
    <Button key="declineConfirm" onClick={handleDecline}>
      {t('declineConfirm')}
    </Button>
  )

  const AcceptButton = (
    <Button key="continue" onClick={handleAccept} disabled={!isChecked} loading={isLoadingConfirm}>
      {isLastStep ? t('accept') : t('acceptAndContinue')}
    </Button>
  )

  return (
    <Dialog
      title={activeAgreement?.title}
      type="utility"
      open
      onClose={onClose}
      ActionButtons={[
        showBackButton && BackButton,
        showDeclineConfirmation ? ConfirmDeclineButton : AcceptButton,
      ]}
    >
      {isLoadingAgreements ? (
        <Box flexCol gap={12} align="center">
          <Box className="gap-0" flex flexCol width="full">
            <RowsSkeletonLoader rows={4} />
          </Box>
        </Box>
      ) : showDeclineConfirmation ? (
        <p className="text-label text-foreground-prominent">{t('declinePrompt')}</p>
      ) : (
        <Box flexCol gap={24} name={PENDING_AGREEMENTS_FORM_NAME}>
          {isErrorConfirm && <Callout variant="error">{submitErrorMessage}</Callout>}

          {activeAgreement?.bodyContent}
          <AgreementLinks flexCol gapY={12}>
            <p className="text-label text-foreground-prominent">{t('readAndApprove')}</p>
            {activeAgreement?.links.map(link => (
              <Box key={link?.label}>
                <Button
                  noPadding
                  variant="ghost"
                  onClick={() => window.open(link?.url, '_blank')}
                  iconElement={<Icon name="ArrowSquareOut" />}
                >
                  {link?.label}
                </Button>
              </Box>
            ))}
          </AgreementLinks>
          <Checkbox
            id="agreement-checkbox"
            name="agreement-checkbox"
            testId="agreement-checkbox"
            checked={isChecked}
            onCheckedChange={() => setIsChecked(current => !current)}
            label={activeAgreement?.checkboxLabel}
          />
          {totalSteps > 1 && (
            <p className="text-foreground-faint text-body-regular-small text-center">
              {currentStep} of {totalSteps}
            </p>
          )}
        </Box>
      )}
    </Dialog>
  )
}
